import React, { useEffect, useState } from "react"
import OutsideClickHandler from "react-outside-click-handler"
import styles from "./FloatingImageEditor.module.scss"
import validator from "validator"
import cs from "classnames"
import { useForm, useWatch } from "react-hook-form"
import { Form } from "@web/components/Forms/Form"
import { validateUrl } from "@web/components/Forms"
import ColorPicker from "@web/components/Forms/ColorPicker"
import { ensureProtocol } from "../../FloatingLinkEditor/FloatingLinkEditor"

export interface Props {
  element: HTMLElement
  className?: string

  onDone: () => void
  onSettingsChange?: (settings: ImageSettingsForm) => void
}

export interface ImageSettingsForm {
  link: string
  isRounded: boolean
  width?: number
  height?: number
  topText?: string
  topTextColor?: string
  bottomText?: string
  bottomTextColor?: string
}

export function FloatingImageEditor({ onDone, element, onSettingsChange, className }: Props) {
  const form = useForm<ImageSettingsForm>({
    defaultValues: {
      isRounded: element.style.borderRadius.includes("50%"),
      link: element.getAttribute("data-link") || "",
      topText: element.getAttribute("data-top-text") || "",
      topTextColor: element.getAttribute("data-top-text-color") || "",
      bottomText: element.getAttribute("data-bottom-text") || "",
      bottomTextColor: element.getAttribute("data-bottom-text-color") || "",
    },
  })

  const [isTopTextColorPickerOpen, setIsTopTextColorPickerOpen] = useState(false)
  const [isBottomTextColorPickerOpen, setIsBottomTextColorPickerOpen] = useState(false)

  const {
    isRounded = false,
    link = "",
    topText,
    topTextColor,
    bottomText,
    bottomTextColor,
  } = useWatch({
    control: form.control,
  })

  const isDirty = form.formState.isDirty

  useEffect(
    function handleImageSettingsChange() {
      if (!isDirty) return

      const trimmedLink = link.trim()

      onSettingsChange?.({
        isRounded,
        link: validator.isURL(trimmedLink) ? ensureProtocol(trimmedLink) : "",
        topText,
        topTextColor,
        bottomText,
        bottomTextColor,
      })
    },
    [bottomText, bottomTextColor, isDirty, isRounded, link, onSettingsChange, topText, topTextColor]
  )

  return (
    <div className="tmb-fixed">
      <OutsideClickHandler onOutsideClick={onDone} disabled={isTopTextColorPickerOpen || isBottomTextColorPickerOpen}>
        <div className={cs(styles.root, className, "tmb-mt-0")}>
          <div className={styles.inner}>
            <Form
              formMethods={form}
              fields={[
                {
                  name: "link",
                  component: "TextField",
                  label: "Link",
                  rules: { validate: validateUrl },
                },
                {
                  name: "topText",
                  component: "TextField",
                  label: "Top Text",
                  after: (
                    <ColorPicker
                      name="topTextColor"
                      className="tmb-ml-4 tmb-border tmb-border-solid tmb-rounded-md"
                      renderValue={false}
                      onOpenChange={setIsTopTextColorPickerOpen}
                      defaultValue={topTextColor || element.getAttribute("data-top-text-color") || "white"}
                    />
                  ),
                  wrapperClassName: "tmb-flex tmb-justify-between tmb-items-center",
                },
                {
                  name: "bottomText",
                  component: "TextField",
                  label: "Bottom Text",
                  after: (
                    <ColorPicker
                      name="bottomTextColor"
                      className="tmb-ml-4 tmb-border tmb-border-solid tmb-rounded-md"
                      renderValue={false}
                      onOpenChange={setIsBottomTextColorPickerOpen}
                      defaultValue={bottomTextColor || element.getAttribute("data-bottom-text-color") || "white"}
                    />
                  ),
                  wrapperClassName: "tmb-flex tmb-justify-between tmb-items-center",
                },
                {
                  name: "isRounded",
                  component: "CheckBox",
                  label: "Rounded",
                  wrapperClassName: "-tmb-mt-4",
                },
              ]}
              className="!tmb-gap-6"
            />
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  )
}
